<template>
  <teleport to="body">
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm p-0 md:p-5"
      @click="handleBackdropClick"
    >
      <div
        :class="[
          'bg-white text-[#0e141b] overflow-hidden shadow-2xl flex flex-col transition-transform',
          presentation === 'mobile_fullscreen'
            ? 'h-full w-full rounded-none'
            : 'mx-auto mt-6 max-w-2xl max-h-[90vh] rounded-3xl border border-gray-200',
          isShaking && 'animate-shake',
        ]"
        @click.stop
      >
        <header
          class="px-5 md:px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3 flex-shrink-0"
        >
          <div>
            <h2 class="text-lg md:text-xl font-bold text-[#0e141b]">Buy SPOTTY Cross-Chain</h2>
            <p class="text-xs md:text-sm text-[#4e7397] mt-1">
              Bridge from EVM or Solana to TON, then swap for SPOTTY
            </p>
            <button
              v-if="showStartNewSwap"
              type="button"
              class="text-xs text-[#1980e6] hover:text-[#166ec5] hover:underline transition-colors mt-1"
              @click="handleStartNewSwap"
            >
              Start New Swap
            </button>
          </div>
          <button
            type="button"
            class="w-9 h-9 rounded-xl border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-[#0e141b] transition-colors flex items-center justify-center"
            @click="closeModal"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </header>

        <div class="px-5 md:px-6 py-4 md:py-5 space-y-4 overflow-y-auto overscroll-y-contain">
          <!-- STON.fi Direct Banner -->
          <a
            href="https://app.ston.fi/swap?chartVisible=false&ft=TON&tt=Spotty"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[#0e141b] hover:bg-gray-100 transition-colors"
          >
            <div
              class="flex-shrink-0 w-8 h-8 rounded-lg bg-[#0088cc]/10 flex items-center justify-center"
            >
              <svg class="w-5 h-5" viewBox="0 0 56 56" fill="none">
                <path
                  d="M28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56Z"
                  fill="#0098EA"
                />
                <path
                  d="M37.5603 15.6277H18.4386C14.9228 15.6277 12.6944 19.4202 14.4632 22.4861L26.2644 42.9409C27.0345 44.2765 28.9644 44.2765 29.7345 42.9409L41.5381 22.4861C43.3045 19.4251 41.0761 15.6277 37.5627 15.6277H37.5603ZM26.2548 36.8068L23.6847 31.8327L17.4833 20.7414C17.0742 20.0315 17.5795 19.1218 18.4362 19.1218H26.2524V36.8092L26.2548 36.8068ZM38.5108 20.739L32.3118 31.8351L29.7417 36.8068V19.1194H37.5579C38.4146 19.1194 38.9199 20.0291 38.5108 20.739Z"
                  fill="white"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm">
                <span class="font-medium">Already have GRAM?</span>
                <span class="text-[#4e7397] ml-1">Buy directly on STON.fi</span>
              </p>
            </div>
            <svg
              class="w-4 h-4 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </a>
          <BuySpottyContent ref="contentRef" variant="modal" :external-action-button="true" />
        </div>

        <footer
          class="px-5 md:px-6 py-4 border-t border-gray-200 flex-shrink-0 flex flex-col gap-2"
        >
          <button
            type="button"
            class="w-full px-6 py-4 rounded-xl bg-[#1980e6] text-white font-semibold text-lg hover:bg-[#166ec5] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 overflow-hidden"
            :disabled="contentRef?.isContinueDisabled || contentRef?.isFlowBusy"
            @click="contentRef?.handleContinue()"
          >
            <svg
              v-if="contentRef?.isFlowBusy"
              class="w-5 h-5 animate-spin flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span class="truncate">{{ contentRef?.continueLabel || 'Get Quote' }}</span>
          </button>
          <button
            v-if="contentRef?.canCancelExecution"
            type="button"
            class="w-full px-6 py-3 rounded-xl border border-[#b42318]/30 bg-white text-[#b42318] font-medium hover:bg-[#b42318]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            @click="contentRef?.handleCancel()"
          >
            {{ contentRef?.isCancellingExecution ? 'Cancelling…' : 'Cancel' }}
          </button>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { useCrosschainBuy } from './composables/useCrosschainBuy'
import { useWallets } from '@/features/crosschain-buy/wallets/use-wallets'
import BuySpottyContent from './BuySpottyContent.vue'
import {
  START_NEW_SWAP_WARNING,
  shouldConfirmStartingNewSwap,
  shouldShowStartNewSwap,
} from '../presentation/start-new-swap-guard'

const { isModalOpen, closeModal, getModalPresentation, state } = useCrosschainBuy()
const wallets = useWallets()

const showStartNewSwap = computed(() => shouldShowStartNewSwap(state.value))

const handleStartNewSwap = async () => {
  if (shouldConfirmStartingNewSwap(state.value) && !window.confirm(START_NEW_SWAP_WARNING)) {
    return
  }
  await contentRef.value?.handleCancel(true)
}

const contentRef = ref<InstanceType<typeof BuySpottyContent>>()
const isShaking = ref(false)

const viewportWidth = ref(1024)

const updateViewportWidth = () => {
  viewportWidth.value = window.innerWidth
}

const presentation = computed(() => getModalPresentation(viewportWidth.value))

const handleBackdropClick = () => {
  isShaking.value = true
  setTimeout(() => {
    isShaking.value = false
  }, 300)
}

watch(isModalOpen, (open) => {
  if (open) {
    updateViewportWidth()
    document.body.style.overflow = 'hidden'
    window.addEventListener('resize', updateViewportWidth)
    window.addEventListener('keydown', handleKeydown)
    wallets.prewarmEvm().catch(() => {
      /* non-critical */
    })
  } else {
    document.body.style.overflow = ''
    window.removeEventListener('resize', updateViewportWidth)
    window.removeEventListener('keydown', handleKeydown)
  }
})

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    closeModal()
  }
}

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('resize', updateViewportWidth)
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-4px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(4px);
  }
}

.animate-shake {
  animation: shake 0.3s ease-in-out;
}
</style>
